/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action, computed } from "mobx";
import { getTotalPages, showToast, isSinglePageMode, isFullScreenOrFitOnePageMode } from "selectors/documentSelectors";
import {
  scrubberPosBasedOnPagesViewPos,
  getActualYOffset,
  getActualXOffset,
  shouldShowScrubber
} from "selectors/pagedViewState";
import { getViewPortHeight, getViewPortWidth } from "selectors/documentViewSelectors";
import { getIsInPlaceMode } from "selectors/viewModeSelectors";
import { isBoolean } from "utils/lodashUtils";
import { getCurrentPageNumber, getCurrentRowIndex } from "selectors/pageNumber";
import { getThumbnailDisplayData } from "selectors/pagedViewSelectors";
import { getYPosition, getXPosition, getPagesViewTotalHeight } from "selectors/pagedViewState";
import { trackPageUp, trackPageDown, trackScroll } from "analytics/events";
import * as constants from "constants/constants";
import {
  getPageOrigHeight,
  getPageOrigWidth,
  getPagesInRow,
  isValidPageIndex,
  getRowHeight,
  getPageHeight
} from "selectors/pageSelectors";
import { getMaxZoom } from "../selectors/zoom";
import { getZoomLevelDimension, getTotalBottomSpace } from "utils/PageUtils";
import { triggerDocumentOpenedEvent } from "publicApis/Events/events";
import { isNumber } from "utils/APIUtils";
import { canvasOperation } from "dc-preview-utils/src/Constants/enums";
import { appIdentifiers } from "constants/enums";
import {
  getViewPortHeightWithoutPagesViewScrollbar,
  getViewPortWidthWithoutPagesViewScrollbar
} from "selectors/documentViewSelectors";

class PagedViewState {
  @observable pagesViewXPos;
  @observable pagesViewYPos;
  @observable thumbnailsViewYPos;
  @observable canvasOperation;
  @observable isInitialPageLoaded;
  @observable textInSelection;
  @observable linkToCopy;
  @observable isPagesViewScrolling;
  @observable isThumbnailsViewScrolling;
  @observable thumbnailIndexForFocus;
  @observable scrubberYPos;
  @observable showScrubberMidDiv;
  @observable showScrubber;
  @observable openAction;
  @observable pagesViewElementHandle;
  @observable nonReactVPagesViewHandle;
  @observable pageAreaRects;
  @observable goToPageIndex;
  @observable isTileCanvasLoaded;
  @observable isInitialPageRendered;
  initialPageIndex;
  thumbnailsViewElementHandle;
  tileCanvasRef;
  pageNumberInputHandle;

  constructor() {
    this.init();
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  init() {
    this.pagesViewXPos = 0;
    this.pagesViewYPos = 0;
    this.thumbnailsViewYPos = 0;
    this.canvasOperation = canvasOperation.CANVAS_OP_NONE;
    this.isInitialPageLoaded = false;
    this.textInSelection = "";
    this.linkToCopy = "";
    this.isPagesViewScrolling = false;
    this.isThumbnailsViewScrolling = false;
    this.thumbnailIndexForFocus = -1;
    this.scrubberYPos = 0;
    this.showScrubberMidDiv = false;
    this.showScrubber = false;
    this.openAction = observable.map({
      pageIndex: undefined,
      x: undefined,
      y: undefined,
      zoomLevel: undefined,
      viewMode: undefined
    });
    this.pagesViewElementHandle = null;
    this.nonReactVPagesViewHandle = null;
    this.pageAreaRects = [];
    this.goToPageIndex = -1;
    this.isInitialPageRendered = false;

    // non-observables
    this.allowOpenActionChange = true;
    this.initialPageIndex = 0;
    this.thumbnailsViewElementHandle = null;
    this.tileCanvasRef = new Map();
    this.isTileCanvasLoaded = false;
  }

  disableOpenActionChange() {
    this.allowOpenActionChange = false;
  }

  @action
  setGoToPageIndex(index) {
    this.goToPageIndex = index;
  }

  @action
  setOpenAction({ pageIndex = 0, x = 0, y = 0, viewMode, zoomLevel }) {
    if (this.allowOpenActionChange) {
      this.openAction.set("pageIndex", pageIndex);
      this.openAction.set("x", x);
      this.openAction.set("y", y);
      if (viewMode) {
        this.openAction.set("viewMode", viewMode);
      }
      if (zoomLevel) {
        this.openAction.set("zoomLevel", zoomLevel);
      }
    }
  }

  @action
  scrollPagesView(newXPos, newYPos, isPagesViewScrolling = false, updateScrubber = true) {
    if (showToast(this.stores) && isPagesViewScrolling && newYPos !== this.pagesViewYPos) {
      this.stores.documentStore.setShouldAutoHideToast(true);
    }
    this.pagesViewXPos = Math.max(newXPos, 0);
    this.pagesViewYPos = Math.max(newYPos, 0);
    const totalPagesHeight = getPagesViewTotalHeight(this.stores);
    // view port is fixed in viewSDK IN_LINE mode.
    if (!getIsInPlaceMode(this.stores)) {
      const totalBottomHeight = getTotalBottomSpace(this.stores);
      const maxScrollPossible = Math.max(totalPagesHeight - (getViewPortHeight(this.stores) - totalBottomHeight), 0);
      if (maxScrollPossible > 0) {
        this.pagesViewYPos = Math.min(maxScrollPossible, this.pagesViewYPos);
      } else {
        this.pagesViewYPos = 0;
      }
    }
    this.isPagesViewScrolling = isPagesViewScrolling;
    this.isThumbnailsViewScrolling = false;
    if (updateScrubber && shouldShowScrubber(this.stores)) {
      if (this.scrubberTimer) clearTimeout(this.scrubberTimer);
      this.scrubberTimer = setTimeout(scrubberPosBasedOnPagesViewPos, 5, this.stores);
    }
    if (isPagesViewScrolling) {
      let thumbnailsDisplayData = getThumbnailDisplayData(this.stores);
      if (thumbnailsDisplayData && thumbnailsDisplayData.isCurrThumbnailVisible === false) {
        this.thumbnailsViewYPos = thumbnailsDisplayData.newThumbnailsViewYPos;
      }
    }
  }

  @action
  scrollPagesViewBy(deltaX, deltaY) {
    this.scrollPagesView(this.pagesViewXPos + deltaX, this.pagesViewYPos + deltaY);
  }

  @action
  scrollThumbnailsView(newYPos, isThumbnailsViewScrolling = false) {
    this.isThumbnailsViewScrolling = isThumbnailsViewScrolling;
    this.thumbnailsViewYPos = newYPos;
    this.isPagesViewScrolling = false;
  }

  @action
  setThumbnailsViewScrolling(isThumbnailsViewScrolling = false) {
    this.isThumbnailsViewScrolling = isThumbnailsViewScrolling;
  }

  @action
  goToPage(pageNum, setGoToIndex = true) {
    if (setGoToIndex) {
      this.goToPageIndex = pageNum - 1;
    } else {
      this.goToPageIndex = -1;
    }
    this.goToLocation(pageNum - 1, 0, 0);
  }

  @action
  goToNextPage() {
    const nextRow = getCurrentRowIndex(this.stores) + 1;
    const pagesInNextRow = getPagesInRow(this.stores, nextRow);
    const nextPageIndex =
      getRowHeight(this.stores, nextRow) > getPageHeight(this.stores, pagesInNextRow.leftPageIndex)
        ? pagesInNextRow.rightPageIndex
        : pagesInNextRow.leftPageIndex;
    if (isValidPageIndex(this.stores, nextPageIndex)) {
      trackPageDown();
      this.goToPage(nextPageIndex + 1, false);
    }
  }

  @action
  goToPrevPage() {
    const prevRow = getCurrentRowIndex(this.stores) - 1;
    const pagesInPrevRow = getPagesInRow(this.stores, prevRow);
    const prevPageIndex =
      getRowHeight(this.stores, prevRow) > getPageHeight(this.stores, pagesInPrevRow.leftPageIndex)
        ? pagesInPrevRow.rightPageIndex
        : pagesInPrevRow.leftPageIndex;
    if (isValidPageIndex(this.stores, prevPageIndex)) {
      trackPageUp();
      this.goToPage(prevPageIndex + 1, false);
    }
  }

  @action
  setThumbnailIndexForFocus(index) {
    if (index === undefined) {
      index = getCurrentPageNumber(this.stores) - 1;
    }
    this.thumbnailIndexForFocus = index;
  }

  @action
  unsetThumbnailIndexForFocus() {
    this.thumbnailIndexForFocus = -1;
  }

  @action
  goToLocation(pageIndex, xInPage, yInPage, pageBottomMargin, scrollOptions = {}) {
    if (pageIndex >= 0 && pageIndex < getTotalPages(this.stores)) {
      if (isSinglePageMode(this.stores)) {
        this.stores.documentStore.setCurrPageIndexForSlideMode(pageIndex);
      } else if (isFullScreenOrFitOnePageMode(this.stores)) {
        this.stores.documentStore.setCurrPageIndexForFitOnePageMode(pageIndex);
      } else {
        const pageHeight = getPageOrigHeight(this.stores, pageIndex);
        const pageWidth = getPageOrigWidth(this.stores, pageIndex);
        if (!isNumber(xInPage) || !(xInPage >= 0 && xInPage <= pageWidth)) {
          xInPage = 0;
        }
        if (!isNumber(yInPage) || !(yInPage >= 0 && yInPage <= pageHeight)) {
          yInPage = 0;
        }

        const newYPos = getYPosition(this.stores, pageIndex, yInPage, pageBottomMargin);
        const newXPos = getXPosition(this.stores, pageIndex, xInPage);
        const totalBottomSpace = getTotalBottomSpace(this.stores);
        const viewPortHeight = getViewPortHeightWithoutPagesViewScrollbar(this.stores) - totalBottomSpace;
        const viewPortWidth = getViewPortWidthWithoutPagesViewScrollbar(this.stores);

        scrollOptions = Object.assign({}, { percScrollOffsetY: 0, percScrollOffsetX: 0 }, scrollOptions);
        if (scrollOptions.noScrollIfVisible) {
          const inRange = (num, start, end) => start <= num && num < end;
          const yInRange = inRange(
            newYPos,
            this.pagesViewYPos + 0.1 * viewPortHeight,
            this.pagesViewYPos + 0.8 * viewPortHeight
          );
          const xInRange = inRange(newXPos, this.pagesViewXPos, this.pagesViewXPos + viewPortWidth);
          if (yInRange && xInRange) return;
        }

        const scrollOffsetX = (viewPortWidth * scrollOptions.percScrollOffsetX) / 100;
        let scrollOffsetY = (viewPortHeight * scrollOptions.percScrollOffsetY) / 100;
        if (scrollOptions.maxScrollOffsetY) {
          scrollOffsetY = Math.min(scrollOffsetY, scrollOptions.maxScrollOffsetY);
        }

        const scrollY = Math.ceil(Math.max(newYPos - scrollOffsetY));
        const scrollX = Math.ceil(Math.max(newXPos - scrollOffsetX));

        this.scrollPagesView(scrollX, scrollY);
      }
    }
  }

  @action
  setInitialPageStatus(pageIndex, value) {
    if (isBoolean(value)) {
      this.isInitialPageLoaded = value;
      this.initialPageIndex = pageIndex;
      if (value) {
        this.stores.accessibilityStore.setDocumentLoadComplete();
        triggerDocumentOpenedEvent();
      }
    }
  }

  @action
  setShowScrubberMidDiv(value) {
    this.showScrubberMidDiv = value;
  }

  @action
  setPageAreaRects(rectsArray) {
    this.pageAreaRects = rectsArray;
  }

  @action
  setShowScrubber(value) {
    this.showScrubber = value;
  }

  @action
  onPagesViewScroll(event) {
    let scrollLeft = event.target ? event.target.scrollLeft : event.scrollLeft;
    let scrollTop = event.target ? event.target.scrollTop : event.scrollTop;
    if (this.stores.documentStore.useReactVirtualized === true && event.target) return;

    if (this.stores.documentStore.useReactVirtualized === true) {
      scrollLeft = getActualXOffset(this.stores, getViewPortWidth(this.stores), scrollLeft);
      scrollTop = getActualYOffset(this.stores, getViewPortHeight(this.stores), scrollTop);
    }

    trackScroll(this.pagesViewXPos, this.pagesViewYPos, scrollLeft, scrollTop);
    // this.stores.appUiState.showImmersiveBehavior(this.pagesViewYPos, scrollTop);
    this.scrollPagesView(scrollLeft, scrollTop, true);
    // this.docApi.setLastPageNum(getCurrentPageNumber(this.stores));
  }

  @action
  updateScrubberYPos(scrubberYPos) {
    this.scrubberYPos = scrubberYPos;
  }

  @computed
  get verticalMaxScrollSize() {
    let height = 0;
    for (let i = 0; i < this.stores.documentStore.pages.length; i++) {
      height +=
        getZoomLevelDimension(getPageOrigHeight(this.stores, i), getMaxZoom(this.stores)) +
        constants.PAGE_BOTTOM_MARGIN;
    }
    return height;
  }

  @action
  resetPagesViewElementHandle() {
    // Used for switching docViewModes
    this.pagesViewElementHandle = null;
  }

  @action
  setPagesViewElementHandle(handle) {
    if (handle) {
      this.pagesViewElementHandle = handle;
    }
  }

  @action
  setAlternatePagesViewElementHandle() {
    this.pagesViewElementHandle = this.nonReactVPagesViewHandle;
  }

  @action
  setNonReactVPagesViewHandle(handle) {
    if (handle) {
      this.nonReactVPagesViewHandle = handle;
    }
  }

  @action.bound
  customizePagedViewStateComponent(componentName, config) {
    if (componentName === appIdentifiers.SCRUBBER) {
      this.setShowScrubber(config.visible);
    }
  }

  setThumbnailsViewElementHandle(handle) {
    this.thumbnailsViewElementHandle = handle;
  }

  @action
  setTileCanvasRef(pageIndex, handle) {
    this.tileCanvasRef.set(pageIndex, handle);
  }

  @action
  setIsTileCanvasLoaded(val) {
    this.isTileCanvasLoaded = val;
  }

  setPageNumberInputHandle(handle) {
    this.pageNumberInputHandle = handle;
  }

  @action
  setIsInitialPageRendered(val) {
    this.isInitialPageRendered = val;
  }
}

export { PagedViewState };
